import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import React, { useState, useEffect, memo } from 'react';
import moment from 'moment-timezone';
import { Flex } from '@magnetic/flex';
import Close from '../../../../../assets/svgs/close-svg';
import { useTranslation } from 'react-i18next';
import Checked from '../../../../../assets/svgs/checked';
import Minus from '../../../../../assets/svgs/minus';
import People from '../../../../../assets/svgs/people';
import Plus from '../../../../../assets/svgs/plus';
import Search from '../../../../../assets/svgs/search';
import { SwitchFloorPopup } from '../switch-floor-popup';
import { getBgColorByStatus, getBorderColorByStatus, STATUSES, STATUS_COLOR_MAP, } from '../../util/index';
import { useDispatch, useSelector } from 'react-redux';
import { setRoomDetailsPopUp, setRoomFinderOpen, } from '../../../../../store/uislice';
import { store } from '../../../../../store/store';
import { closeRoomDetailsPopup } from '../room-details-popup';
import { getContext, getFloorAnalytics, getLocation, getRoomAnalytics, getSelectedLocation, setSelectedLocation, } from '../../../redux/spaces-slice';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
const CAPACITY_MAX = 20;
const CAPACITY_MIN = 1;
const AVAILABLE_ALL_DAY_WINDOW_SECONDS = 24 * 60 * 60;
const buildFreeUntilByRoom = (floorAnalytics) => {
    var _a, _b, _c;
    const freeUntilByRoom = {};
    for (const floorId of Object.keys(floorAnalytics)) {
        const freeUntil = (_c = (_b = (_a = floorAnalytics[floorId]) === null || _a === void 0 ? void 0 : _a.availabilityInfo) === null || _b === void 0 ? void 0 : _b.roomInfo) === null || _c === void 0 ? void 0 : _c.freeUntil;
        if (!freeUntil) {
            continue;
        }
        for (const roomKey of Object.keys(freeUntil)) {
            freeUntilByRoom[roomKey] = Number(freeUntil[roomKey]);
        }
    }
    return freeUntilByRoom;
};
const buildAvailabilityWindowByRoom = (floorAnalytics, nowInSeconds) => {
    var _a, _b, _c, _d;
    const availabilityWindowByRoom = {};
    const shouldUseAvailableUntil = Object.keys(floorAnalytics).some((floorId) => {
        var _a, _b, _c;
        return Object.prototype.hasOwnProperty.call((_c = (_b = (_a = floorAnalytics[floorId]) === null || _a === void 0 ? void 0 : _a.availabilityInfo) === null || _b === void 0 ? void 0 : _b.roomInfo) !== null && _c !== void 0 ? _c : {}, 'availableUntil');
    });
    for (const floorId of Object.keys(floorAnalytics)) {
        const roomInfo = (_b = (_a = floorAnalytics[floorId]) === null || _a === void 0 ? void 0 : _a.availabilityInfo) === null || _b === void 0 ? void 0 : _b.roomInfo;
        const availableUntil = (_c = roomInfo === null || roomInfo === void 0 ? void 0 : roomInfo.availableUntil) !== null && _c !== void 0 ? _c : {};
        const freeUntil = (_d = roomInfo === null || roomInfo === void 0 ? void 0 : roomInfo.freeUntil) !== null && _d !== void 0 ? _d : {};
        if (shouldUseAvailableUntil) {
            for (const roomKey of Object.keys(availableUntil)) {
                const nextMeetingStartTime = availableUntil[roomKey];
                const secondsUntilNextMeeting = nextMeetingStartTime - nowInSeconds;
                availabilityWindowByRoom[roomKey] = {
                    secondsUntilNextMeeting: secondsUntilNextMeeting > AVAILABLE_ALL_DAY_WINDOW_SECONDS
                        ? Infinity
                        : secondsUntilNextMeeting,
                    nextMeetingStartTime,
                    freeUntilMinutes: null,
                    usesLegacyFreeUntil: false,
                };
            }
        }
        else {
            for (const roomKey of Object.keys(freeUntil)) {
                availabilityWindowByRoom[roomKey] = {
                    secondsUntilNextMeeting: Number(freeUntil[roomKey]) * 60,
                    nextMeetingStartTime: null,
                    freeUntilMinutes: Number(freeUntil[roomKey]),
                    usesLegacyFreeUntil: true,
                };
            }
        }
    }
    return availabilityWindowByRoom;
};
const isMeetingAtNextLocalMidnight = (nextMeetingStartTime, nowInSeconds, timeZoneId) => {
    if (!nextMeetingStartTime) {
        return false;
    }
    const nextMeetingDate = moment.unix(nextMeetingStartTime).tz(timeZoneId);
    const nextLocalMidnight = moment
        .unix(nowInSeconds)
        .tz(timeZoneId)
        .startOf('day')
        .add(1, 'day');
    return nextMeetingDate.valueOf() === nextLocalMidnight.valueOf();
};
const formatAvailabilityWindow = (availabilityWindow, nowInSeconds, timeZoneId, t) => {
    if (!availabilityWindow) {
        return t('floor.room-finder.available-all-day');
    }
    if (availabilityWindow.usesLegacyFreeUntil) {
        return `${availabilityWindow.freeUntilMinutes} ${t('floor.room-details-popup.min')}`;
    }
    if (isMeetingAtNextLocalMidnight(availabilityWindow.nextMeetingStartTime, nowInSeconds, timeZoneId)) {
        return t('floor.room-finder.available-all-day');
    }
    if (!Number.isFinite(availabilityWindow.secondsUntilNextMeeting)) {
        return t('floor.room-finder.available-all-day');
    }
    const nextMeetingTime = new Date(availabilityWindow.nextMeetingStartTime * 1000).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timeZoneId,
    });
    const meridiem = nextMeetingTime.slice(-2);
    const localizedNextMeetingTime = nextMeetingTime.slice(0, -2) + t(meridiem);
    return `${t('floor.room-finder.available-until')} ${localizedNextMeetingTime}`;
};
const Text = styled(Flex) `
  font-size: ${(props) => props.fontSize};
  color: ${(props) => props.color};
  background: ${(props) => props.background};
  padding: ${(props) => props.padding};
`;
const ShowOnMapToggleContainer = styled(Flex) `
  height: 3.6vw;
  margin-top: 0vw;
  background: #1f2226;
  border-radius: 0.8vw;
  padding-top: 1vw;
`;
const Toggle = styled(Flex) `
  height: 2vw;
  width: 4.2vw;
  text-align: center;
  font-size: 0.76vw;
  padding-top: 0.6vw;
  background: ${(props) => props.background};
  border-radius: ${(props) => props.borderRadius};
  z-index: 100;
  cursor: pointer;
`;
const ToggleContainer = styled(Flex) `
  background: #000000;
  height: fit-content;
  width: fit-content;
  border-radius: 0.6vw;
  margin-top: 0.4vw;
`;
const RoomFinderContainer = styled.div `
  height: fit-content;
  z-index: 99999;
`;
const Line = styled.div `
  background: #9e9ea2;
  height: 0.06vw;
  margin-top: 0.8vw;
`;
const Switch = styled(Flex) `
  width: 3.75vw;
  height: fit-content;
  background: ${(props) => props.isChecked
    ? 'linear-gradient(180deg, #113d7d 0%, #1d7ff3 100%)'
    : '#31363E'};
  border-radius: 1.25vw;
  align-items: center;
  cursor: pointer;
  padding: 0.2vw 0vw;
`;
const Circle = styled(Flex) `
  height: 1.25vw;
  width: 1.25vw;
  margin: 0vw 0.125vw;
  border-radius: 1.25vw;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Capacity = styled(Flex) `
  border-radius: 0.6vw;
  padding: 0.6vw 0vw;
  width: 10vw;
  background: black;
  margin-top: -0.4vw;
`;
const SelectCapacityContainer = styled(Flex) `
  margin-top: 2vw;
`;
const SelectTelepresenseContainer = styled(Flex) `
  margin-top: 1vw;
`;
const SearchBarContainer = styled(Flex) `
  background: black;
  padding: 0.6vw;
  border-radius: 0.6vw;
  margin-top: 1vw;
  cursor: pointer;
`;
const ResultsContainer = styled(Flex) ``;
const Result = styled(Flex) `
  height: fit-content;
  padding: 0.4vw;
  background: ${(props) => props.background};
  width: 100%;
  margin: ${(props) => props.margin};
`;
const AvailableCircle = styled(Flex) `
  width: 0.52vw;
  height: 0.52vw;
  margin-right: 0.375vw;
  background: ${(props) => getBgColorByStatus(props.availability)};
  border: 0.1vw solid ${(props) => getBorderColorByStatus(props.availability)};
  border-radius: 0.6875vw;
`;
const SelectAvailability = ({ t, availability, setAvailability }) => {
    const availabilityStatuses = ['Any', '30', '60', '90'];
    return (_jsxs(ShowOnMapToggleContainer, Object.assign({ direction: "vertical", justify: "space-around" }, { children: [_jsx(Flex, Object.assign({ direction: "vertical" }, { children: _jsx(Text, Object.assign({ fontSize: "0.8vw", color: "#9e9ea2" }, { children: t('floor.room-finder.availability-status') })) })), _jsx(ToggleContainer, Object.assign({ justify: "space-between" }, { children: availabilityStatuses.map((availabilityStatus, i) => (_jsx(Toggle, Object.assign({ className: "room_finder_availability_option", direction: "vertical", borderRadius: "0.6vw", background: availability === availabilityStatus
                        ? 'linear-gradient(180deg, #1D7FF3 0%, #113D7D 100%)'
                        : '#000000', onClick: () => setAvailability(availabilityStatus) }, { children: availabilityStatus === 'Any'
                        ? t('floor.room-search-popup.any')
                        : `${availabilityStatus}` +
                            t('floor.room-search-popup.min') +
                            ' +' }), i))) }))] })));
};
const SelectCapacity = ({ t, minCapacity, setMinCapacity }) => {
    const PlusHandler = () => {
        if (minCapacity < CAPACITY_MAX) {
            setMinCapacity((minCapacity) => minCapacity + 1);
        }
    };
    const MinusHandler = () => {
        if (minCapacity > CAPACITY_MIN) {
            setMinCapacity((minCapacity) => minCapacity - 1);
        }
    };
    return (_jsxs(SelectCapacityContainer, Object.assign({ justify: "space-between" }, { children: [_jsx(Text, Object.assign({ fontSize: "0.8vw", color: "#9e9ea2" }, { children: t('floor.room-finder.room-capacity') })), _jsxs(Capacity, Object.assign({ justify: "space-evenly" }, { children: [_jsx("div", Object.assign({ id: "room_capacity_minus", onClick: MinusHandler }, { children: _jsx(Minus, {}) })), _jsx(People, {}), _jsxs(Text, Object.assign({ fontSize: "0.8vw" }, { children: [minCapacity, " ", t('floor.room-finder.or-more')] })), _jsx("div", Object.assign({ id: "room_capacity_plus", onClick: PlusHandler }, { children: _jsx(Plus, {}) }))] }))] })));
};
const SelectTelepresense = ({ t, telepresense, setTelepresense }) => {
    return (_jsxs(SelectTelepresenseContainer, Object.assign({ justify: "space-between" }, { children: [_jsx(Text, Object.assign({ fontSize: "0.8vw", color: "#9e9ea2" }, { children: t('floor.room-finder.with-telepresense-device') })), _jsx(Switch, Object.assign({ id: "collabDevice_toggle", isChecked: telepresense, onClick: () => setTelepresense(!telepresense), justify: telepresense ? 'end' : 'start' }, { children: _jsx(Circle, { children: _jsx(Checked, {}) }) }))] })));
};
const SearchBar = ({ t, onClick }) => {
    return (_jsxs(SearchBarContainer, Object.assign({ id: "room_finder_search_input", onClick: onClick }, { children: [_jsx(Search, {}), _jsxs(Text, Object.assign({ fontSize: "0.8vw" }, { children: [" \u00A0 ", t('floor.room-finder.search')] }))] })));
};
const Results = ({ t, rooms, availability, telepresense, minCapacity, resultClickHandler, currentFloor, availabilityWindowByRoom, currentTimeInSeconds, timeZoneId, isSingleFloorSignage, }) => {
    const roomsOnThisFloor = rooms.filter((room) => room.floorNumber === currentFloor);
    const roomsNotOnThisFloor = rooms.filter((room) => room.floorNumber !== currentFloor);
    const showRoomsList = (roomsList) => {
        return roomsList.map((room, i) => (_jsxs(Result, Object.assign({ background: i % 2 && '#171b1d', margin: '0 0 0 -0.4vw', direction: "vertical", onClick: () => resultClickHandler(room.name, room.floorNumber, room.key) }, { children: [_jsx(Text, Object.assign({ fontSize: "0.8vw" }, { children: room.name })), _jsxs(Flex, Object.assign({ style: { fontSize: '0.68vw', marginTop: '0.2vw' }, justify: "space-between" }, { children: [_jsxs(Flex, Object.assign({ width: "7.6vw", minWidth: "fit-content" }, { children: [_jsx(AvailableCircle, { availability: room.availability }), _jsxs(Text, Object.assign({ color: STATUS_COLOR_MAP[room.availability] }, { children: [t(`floor.room-details-popup.${room.availability}`), ' ', room.availability === STATUSES.AVAILABLE &&
                                            formatAvailabilityWindow(availabilityWindowByRoom === null || availabilityWindowByRoom === void 0 ? void 0 : availabilityWindowByRoom[room.key], currentTimeInSeconds, timeZoneId, t)] })), _jsxs(Text, { children: ["\u00A0", room.duration] })] })), _jsxs(Flex, Object.assign({ width: "3.2vw" }, { children: [_jsxs(Text, Object.assign({ style: { verticalAlign: '-0.2vw' }, fontSize: "0.6vw", color: "#9e9ea2" }, { children: [t('floor.room-finder.capacity'), " "] })), _jsxs(Text, Object.assign({ style: { verticalAlign: '-0.2vw' }, fontSize: "0.6vw", color: "#00BCEB" }, { children: ["\u00A0", room.capacity] }))] })), _jsxs(Flex, { children: [_jsxs(Text, Object.assign({ style: { verticalAlign: '-0.2vw' }, fontSize: "0.6vw", color: "#9e9ea2" }, { children: [t('floor.room-finder.floor'), " "] })), _jsxs(Text, Object.assign({ style: { verticalAlign: '-0.2vw' }, fontSize: "0.6vw" }, { children: ["\u00A0", room.floorNumber] }))] })] }))] }), i)));
    };
    return (_jsxs(ResultsContainer, Object.assign({ direction: "vertical" }, { children: [_jsx(Text, Object.assign({ padding: "1vw 0vw", fontSize: "1vw", color: "#9e9ea2" }, { children: t('floor.room-finder.on-this-floor') })), showRoomsList(roomsOnThisFloor), _jsx(Text, Object.assign({ fontSize: "1vw", color: "#9e9ea2", padding: "1vw 0vw" }, { children: t('floor.room-finder.similar-results') })), !isSingleFloorSignage && showRoomsList(roomsNotOnThisFloor)] })));
};
function compareName(room1, room2) {
    const name1 = room1.name.toUpperCase();
    const name2 = room2.name.toUpperCase();
    let comparison = 0;
    if (name1 > name2) {
        comparison = 1;
    }
    else if (name1 < name2) {
        comparison = -1;
    }
    return comparison;
}
const getRichMapIDCapacityMap = (locationData) => {
    const richMapIdCapacityMap = {};
    for (const child of locationData === null || locationData === void 0 ? void 0 : locationData.children) {
        const rooms = child === null || child === void 0 ? void 0 : child.rooms;
        if (!rooms)
            continue;
        for (const room of rooms) {
            richMapIdCapacityMap[room.richMapId] = Math.max(room.capacity, 1);
        }
    }
    return richMapIdCapacityMap;
};
export const fillAnalyticalInfoToRooms = (globalRoomsData, floorAnalytics, locationData) => {
    var _a, _b;
    const richMapIdCapacityMap = getRichMapIDCapacityMap(locationData);
    const modifiedGlobalRoomsData = [];
    let availableRooms = [];
    let bookedRooms = [];
    let occupiedRooms = [];
    let holdRooms = [];
    for (const floorLocationId in floorAnalytics) {
        const oneFloorAnalytics = floorAnalytics[floorLocationId];
        availableRooms = availableRooms.concat(oneFloorAnalytics.availabilityInfo.roomInfo.available);
        bookedRooms = bookedRooms.concat(oneFloorAnalytics.availabilityInfo.roomInfo.booked);
        occupiedRooms = occupiedRooms.concat(oneFloorAnalytics.availabilityInfo.roomInfo.occupied);
        holdRooms = holdRooms.concat((_b = (_a = oneFloorAnalytics.availabilityInfo.roomInfo) === null || _a === void 0 ? void 0 : _a.hold) !== null && _b !== void 0 ? _b : []);
    }
    for (const room of globalRoomsData) {
        const newRoom = Object.assign({}, room);
        if (availableRooms.includes(room.key)) {
            newRoom.availability = 'Available';
        }
        else if (bookedRooms.includes(room.key)) {
            newRoom.availability = 'Booked';
        }
        else if (occupiedRooms.includes(room.key)) {
            newRoom.availability = 'Occupied';
        }
        else if (holdRooms.includes(room.key)) {
            newRoom.availability = 'Hold';
        }
        newRoom.capacity = richMapIdCapacityMap[room.key]
            ? richMapIdCapacityMap[room.key]
            : 'Unknown';
        modifiedGlobalRoomsData.push(newRoom);
    }
    return modifiedGlobalRoomsData;
};
const getAllRoomsFromStore = (locationData) => {
    const rooms = [];
    for (const floorData of locationData.children) {
        for (const room of floorData.rooms) {
            rooms.push({
                name: room.name,
                availability: 'Unknown',
                floorNumber: floorData.floorNumber,
                key: room.richMapId,
                capacity: room.capacity !== 0 ? room.capacity : 1,
            });
        }
    }
    return rooms;
};
const RoomFinder = memo(({ floorId, onClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const locationData = useSelector(getLocation);
    const contextInfo = useSelector(getContext);
    let globalRoomsData = [];
    const floorLocationId = useSelector(getSelectedLocation);
    const floorLocationData = locationData.children.find((child) => child.id === floorLocationId);
    const currentFloor = floorLocationData === null || floorLocationData === void 0 ? void 0 : floorLocationData.floorNumber;
    const { t } = useTranslation();
    const [availability, setAvailability] = useState('Any');
    const [telepresense, setTelepresense] = useState(true);
    const [minCapacity, setMinCapacity] = useState(1);
    const [showSwitchFloorPopup, setShowSwitchFloorPopup] = useState(false);
    const [roomNameForSwitchFloor, setRoomNameForSwitchFloor] = useState(null);
    const [floorNumberForSwitchFloor, setFloorNumberForSwitchFloor] = useState(null);
    const [selectedRichMapId, setSelectedRichMapId] = useState(null);
    const floorAnalytics = useSelector(getFloorAnalytics);
    const allRoomAnalytics = useSelector(getRoomAnalytics);
    const [availabilityWindowByRoom, setAvailabilityWindowByRoom] = useState({});
    const [freeUntilByRoom, setFreeUntilByRoom] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const floorNumber = contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.floorNumber;
    const timeZoneId = (contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.timeZoneId) || 'Etc/UTC';
    useEffect(() => {
        setAvailabilityWindowByRoom(buildAvailabilityWindowByRoom(floorAnalytics, Math.floor(Date.now() / 1000)));
        setFreeUntilByRoom(buildFreeUntilByRoom(floorAnalytics));
    }, [floorAnalytics]);
    const initRooms = () => {
        setIsLoading(true);
        globalRoomsData = [...getAllRoomsFromStore(locationData)];
        globalRoomsData.sort(compareName);
        // Provide availability, capacity info to the rooms for which the info is available
        globalRoomsData = fillAnalyticalInfoToRooms(globalRoomsData, floorAnalytics, locationData);
        setIsLoading(false);
    };
    useEffect(() => {
        initRooms();
        const newRooms = globalRoomsData.filter((room) => {
            var _a, _b;
            if ((room === null || room === void 0 ? void 0 : room.availability) !== STATUSES.AVAILABLE && availability !== 'Any')
                return false;
            if (allRoomAnalytics[room.key]) {
                return (room.capacity >= minCapacity &&
                    ((_a = allRoomAnalytics[room.key]) === null || _a === void 0 ? void 0 : _a.telepresence) === telepresense &&
                    (availability === 'Any' ||
                        ((_b = freeUntilByRoom === null || freeUntilByRoom === void 0 ? void 0 : freeUntilByRoom[room.key]) !== null && _b !== void 0 ? _b : -1) >= Number(availability)));
            }
            return room.capacity >= minCapacity; // Room finder has a capacity filter, hence choosing not to display non-webex rooms here
        });
        newRooms.sort(compareName);
        setRooms(newRooms);
    }, [
        availability,
        telepresense,
        minCapacity,
        floorAnalytics,
        allRoomAnalytics,
        availabilityWindowByRoom,
        freeUntilByRoom,
    ]);
    const [rooms, setRooms] = useState(globalRoomsData);
    const isSingleFloorSignage = contextInfo === null || contextInfo === void 0 ? void 0 : contextInfo.floorNumber;
    const resultClickHandler = (name, floorNuber, key) => {
        setSelectedRichMapId(key);
        if (floorNuber !== currentFloor) {
            setRoomNameForSwitchFloor(name);
            setFloorNumberForSwitchFloor(floorNuber);
            setShowSwitchFloorPopup(true);
            closeRoomDetailsPopup();
        }
        else {
            window.rMap.focusFeature(key);
            store.dispatch(setRoomDetailsPopUp({ payload: key }));
        }
    };
    const enableRoomSearchPopup = () => {
        var _a;
        (_a = document.getElementById('search_button')) === null || _a === void 0 ? void 0 : _a.click();
    };
    const closeSwitchFloorPopup = (shouldDispatchLocator) => {
        var _a;
        setShowSwitchFloorPopup(false);
        closeRoomDetailsPopup();
        if (document.getElementById('quit-search')) {
            (_a = document.getElementById('quit-search')) === null || _a === void 0 ? void 0 : _a.click();
        }
        if (shouldDispatchLocator) {
            setTimeout(() => {
                store.dispatch(setRoomDetailsPopUp({ payload: selectedRichMapId }));
                window.rMap.focusFeature(selectedRichMapId);
            }, 1000);
        }
        setSelectedRichMapId(null);
    };
    return (_jsxs(RoomFinderContainer, { children: [showSwitchFloorPopup && (_jsx(SwitchFloorPopup, { name: roomNameForSwitchFloor, floorNumber: floorNumberForSwitchFloor, selectFloor: (selectedFloorId) => {
                    // navigate('/floor', { state: { id: selectedFloorId } });
                    dispatch(setSelectedLocation(selectedFloorId));
                    store.dispatch(setRoomFinderOpen({ payload: false }));
                    closeSwitchFloorPopup(true);
                }, onClose: () => closeSwitchFloorPopup(false), isPOI: false })), _jsxs(Flex, Object.assign({ justify: "space-between", style: { marginTop: '1vw' } }, { children: [_jsx(Text, Object.assign({ fontSize: "1.2vw" }, { children: t('floor.counts-widget.room-finder') })), _jsx("div", Object.assign({ id: "room_finder_close", onClick: onClose }, { children: _jsx(Close, {}) }))] })), _jsx(Line, {}), _jsx(SelectAvailability, { t: t, availability: availability, setAvailability: setAvailability }), _jsx(SelectCapacity, { t: t, minCapacity: minCapacity, setMinCapacity: setMinCapacity }), _jsx(SelectTelepresense, { t: t, telepresense: telepresense, setTelepresense: setTelepresense }), _jsx(SearchBar, { t: t, onClick: enableRoomSearchPopup }), _jsx(Line, {}), !isLoading ? (_jsx(Results, { t: t, rooms: rooms, availability: availability, telepresense: telepresense, minCapacity: minCapacity, resultClickHandler: resultClickHandler, currentFloor: currentFloor, availabilityWindowByRoom: availabilityWindowByRoom, currentTimeInSeconds: Math.floor(Date.now() / 1000), timeZoneId: timeZoneId, isSingleFloorSignage: isSingleFloorSignage })) : (_jsx(Flex, Object.assign({ justify: "center", align: "center", style: { height: '10vw' } }, { children: _jsx(Text, Object.assign({ fontSize: "1.2vw", color: "#9e9ea2" }, { children: t('floor.room-finder.loading') })) })))] }));
});
export default RoomFinder;
